/**
 * An inclusive window of calendar days, the way api-core's `from`/`to`
 * windows take it: YYYY-MM-DD days at a timezone offset, at most a year
 * apart. The transfer audits and missing scans pages both search by one
 * (web-portal#71).
 */
export interface DayWindow {
  from: string
  to: string
}

const DAY_MS = 24 * 60 * 60 * 1000

// The most days a window may span; api-core refuses a longer one.
const DAY_WINDOW_MAX_DAYS = 366

const DAY = /^\d{4}-\d{2}-\d{2}$/

function dayOf(iso: string): number | null {
  if (!DAY.test(iso)) return null
  const date = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? null : date.getTime()
}

/** Why a window cannot be searched, or null when it can. The same rules api-core applies. */
export function dayWindowError(window: DayWindow): string | null {
  const start = dayOf(window.from)
  const end = dayOf(window.to)
  if (start === null || end === null) return 'Choose a from and to date.'
  if (start > end) return 'The from date must be on or before the to date.'
  if ((end - start) / DAY_MS > DAY_WINDOW_MAX_DAYS) return 'Keep the window to a year or less.'
  return null
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** A date as the viewer's calendar day, YYYY-MM-DD. */
export function localDay(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** The last 30 days, ending today, in the viewer's zone. */
export function defaultDayWindow(now = new Date()): DayWindow {
  return { from: localDay(new Date(now.getTime() - 30 * DAY_MS)), to: localDay(now) }
}

/**
 * The viewer's UTC offset on a day, as api-core's `tz` takes it (-06:00,
 * +05:30), so the window's days are the viewer's days. Taken at the start
 * of the window's first day, which is where a daylight-saving change would
 * otherwise shift it.
 */
export function tzOffset(window: DayWindow): string {
  const start = dayOf(window.from)
  const at = start === null ? new Date() : new Date(window.from.replace(DAY, '$&T00:00:00'))
  const minutes = -at.getTimezoneOffset()
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}

/** The window a page's URL names, or null when it names none or a bad one. */
export function dayWindowFromParams(params: URLSearchParams): DayWindow | null {
  const window = { from: params.get('from') ?? '', to: params.get('to') ?? '' }
  return dayWindowError(window) ? null : window
}

/** A window as a page's URL params. */
export function dayWindowToParams(window: DayWindow): Record<string, string> {
  return { from: window.from, to: window.to }
}

/** "Sep 1 to Sep 30, 2026": a window named for a heading or an empty state. */
export function describeDayWindow(window: DayWindow): string {
  const format = (iso: string, withYear: boolean) => {
    const date = new Date(`${iso}T00:00:00`)
    if (Number.isNaN(date.getTime())) return iso
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(withYear && { year: 'numeric' }),
    })
  }
  return `${format(window.from, window.from.slice(0, 4) !== window.to.slice(0, 4))} to ${format(window.to, true)}`
}
